import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { tenantTokenUsageService } from "../services/tenantTokenUsageService";
import { tenantTokenUsageDisplayId } from "./TenantTokenUsageCommonStore";
import { TenantTokenUsage } from "../../../api/graphql";
import { aiSearchConversationDisplayId } from "../../aiSearchConversation/store/AiSearchConversationCommonStore";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { aiSearchConversationService } from "../../aiSearchConversation/services/aiSearchConversationService";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

export class TenantTokenUsageViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = tenantTokenUsageService;
  tenantTokenUsageInstance: TenantTokenUsage | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  providerValue: string = "";
  modelValue: string = "";
  promptTokensValue: number = 0;
  completionTokensValue: number = 0;
  totalTokensValue: number = 0;
  conversationService = aiSearchConversationService;
  conversationSearchValue: string = "";
  conversationIdValue: string = "";
  conversationSelectedValue: { displayId: string; id: string } | null = null;
  conversationUserService = userService;
  conversationUserSearchValue: string = "";
  conversationUserIdValue: string = "";
  conversationUserSelectedValue: { displayId: string; id: string } | null =
    null;
  timestampValue: Date | null = null;
  totalTimeMsValue: number = 0;
  llmTimeMsValue: number = 0;
  toolsTimeMsValue: number = 0;
  ragTimeMsValue: number | undefined = undefined;
  firstTokenMsValue: number | undefined = undefined;
  llmCallCountValue: number = 0;
  toolCallCountValue: number = 0;
  ragChunksCountValue: number | undefined = undefined;
  abTestConditionValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadTenantTokenUsageById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantTokenUsageOne({ id });
    this.tenantTokenUsageInstance = result.tenantTokenUsageOne;
    if (this.tenantTokenUsageInstance) {
      this.loadValues(this.tenantTokenUsageInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.tenantTokenUsageInstance) {
      return tenantTokenUsageDisplayId(
        this.tenantTokenUsageInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantTokenUsageSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "conversation") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.aiSearchConversationOne({
          id: params.returnData.resultId,
        });
        const aiSearchConversation = result.aiSearchConversationOne;
        this.conversationSelectedValue = aiSearchConversation;
      } else if (params.callerData?.fieldName === "conversationUser") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.conversationUserSelectedValue = user;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get conversationValue() {
    if (this.conversationSelectedValue) {
      return {
        id: this.conversationSelectedValue.id,
        displayId: aiSearchConversationDisplayId(
          this.conversationSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get conversationUserValue() {
    if (this.conversationUserSelectedValue) {
      return {
        id: this.conversationUserSelectedValue.id,
        displayId: userDisplayId(
          this.conversationUserSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.tenantTokenUsageInstance = null;
    this.providerValue = "";
    this.modelValue = "";
    this.promptTokensValue = 0;
    this.completionTokensValue = 0;
    this.totalTokensValue = 0;
    this.conversationSearchValue = "";
    this.conversationIdValue = "";
    this.conversationUserSearchValue = "";
    this.conversationUserIdValue = "";
    this.timestampValue = null;
    this.totalTimeMsValue = 0;
    this.llmTimeMsValue = 0;
    this.toolsTimeMsValue = 0;
    this.ragTimeMsValue = undefined;
    this.firstTokenMsValue = undefined;
    this.llmCallCountValue = 0;
    this.toolCallCountValue = 0;
    this.ragChunksCountValue = undefined;
    this.abTestConditionValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelTenantTokenUsage() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editTenantTokenUsage() {
    const id = this.tenantTokenUsageInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/tenantTokenUsage/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printTenantTokenUsage(handlePrint: () => void): void {
    handlePrint();
  }

  *closeTenantTokenUsage(): any {
    yield this.close();
  }

  loadValues(instance: TenantTokenUsage) {
    this.idValue = instance.id;
    this.providerValue = instance.provider;
    this.modelValue = instance.model;
    this.promptTokensValue = instance.promptTokens;
    this.completionTokensValue = instance.completionTokens;
    this.totalTokensValue = instance.totalTokens;
    this.conversationSelectedValue = instance.conversation
      ? instance.conversation
      : null;
    this.conversationUserSelectedValue = instance.conversationUser;
    this.timestampValue = new Date(instance.timestamp);
    this.totalTimeMsValue = instance.totalTimeMs;
    this.llmTimeMsValue = instance.llmTimeMs;
    this.toolsTimeMsValue = instance.toolsTimeMs;
    this.ragTimeMsValue = instance.ragTimeMs || undefined;
    this.firstTokenMsValue = instance.firstTokenMs || undefined;
    this.llmCallCountValue = instance.llmCallCount;
    this.toolCallCountValue = instance.toolCallCount;
    this.ragChunksCountValue = instance.ragChunksCount || undefined;
    this.abTestConditionValue = instance.abTestCondition || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadTenantTokenUsageById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
